import {Collapse} from '@mantine/core';
import {useDisclosure} from '@mantine/hooks';
import {BasicButton} from '@packages/ui';
import {type FC, useMemo} from 'react';
import {IoClose} from 'react-icons/io5';
import {MdExpandLess} from 'react-icons/md';
import {TbGridDots} from 'react-icons/tb';
import type {ActiveCategory} from '.';
import type {GetColumnCategories} from '../../../../../../../core/repositories/columnRepository';
import {cn} from '../../../../../../../lib/classname';
import {CommonLink} from '../../../../../../../shared/components/CommonLink';
import {
  COLUMNS_BY_ALL_CATEGORIES,
  COLUMNS_BY_ROOT_CATEGORY,
  COLUMNS_BY_SUB_CATEGORY,
} from '../../../routes';

type Props = {
  categories: GetColumnCategories['categories'];
  activeRootCategory?: ActiveCategory;
  activeSubCategory?: ActiveCategory;
};
export const CategoriesSP: FC<Props> = ({
  categories,
  activeRootCategory,
  activeSubCategory,
}) => {
  const [opened, {toggle, close}] = useDisclosure(false);

  const currentActiveCategoryString = useMemo(() => {
    if (activeSubCategory) {
      return `${activeSubCategory.item.name}`;
    }
    if (activeRootCategory) {
      return activeRootCategory.item.name;
    }
    return null;
  }, [activeRootCategory, activeSubCategory]);

  return (
    <div className={cn('')}>
      <div className="px-16 pt-12 pb-0 flex items-center justify-between border-t border-black-5">
        <button
          type="button"
          onClick={toggle}
          className="text-sm font-bold flex items-center"
        >
          {opened ? (
            <span className="inline-flex items-center gap-x-4">
              <MdExpandLess size="24px" aria-hidden="true" />
              <span>閉じる</span>
            </span>
          ) : (
            <span className="inline-flex items-center gap-x-4">
              <TbGridDots size="20px" aria-hidden="true" />
              <span>カテゴリを選ぶ</span>
            </span>
          )}
        </button>
        <div>
          {currentActiveCategoryString && (
            <div className="inline-flex items-center">
              <CommonLink
                href={COLUMNS_BY_ALL_CATEGORIES}
                className={cn(
                  'inline-flex items-center gap-x-4',
                  'text-sm font-bold text-primary-1 bg-background-2 px-8 py-4 rounded-16 border border-primary-1',
                )}
              >
                {currentActiveCategoryString}
                <IoClose
                  size="18px"
                  className=""
                  aria-label="このカテゴリの選択を解除する"
                />
              </CommonLink>
            </div>
          )}
        </div>
      </div>
      <Collapse in={opened}>
        <div className="flex flex-col gap-8">
          <ul className="py-8 px-16 list-none text-primary-1 text-base space-y-8">
            {categories.map((root) => (
              <li key={root.category.slug}>
                <CommonLink
                  href={COLUMNS_BY_ROOT_CATEGORY(root.category.slug)}
                  className={cn('hover:opacity-60')}
                  onClick={close}
                >
                  {root.category.name}
                </CommonLink>
                {root.children.length >= 2 && (
                  <ul className="ml-[24px] list-disc text-primary-1 text-sm mt-4 space-y-4">
                    {root.children.map((sub) => (
                      <li key={sub.category.slug}>
                        <CommonLink
                          href={COLUMNS_BY_SUB_CATEGORY(
                            root.category.slug,
                            sub.category.slug,
                          )}
                          className={cn('hover:opacity-60')}
                          onClick={close}
                        >
                          {sub.category.name}
                        </CommonLink>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
          <div className="px-16">
            <BasicButton onClick={close}>閉じる</BasicButton>
          </div>
        </div>
      </Collapse>
    </div>
  );
};
